'use client';

import React, { useLayoutEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import classnames from 'classnames';
import { Button } from '@/components/common/Button';
import { Icon } from '@/components/common/Icon';
import { useCurrentOrganization } from '@/core/hooks/useCurrentOrganization';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { getPagesByUserPermissions } from '@/lib/getPagesByUserPermissions';
import { NavigationRoute, getNavigationRoutes } from '@/lib/navigationRoutes';
import { PageHref, PageTitleMode, getPageTitle } from '@/lib/routesMetadata';
import { Colors } from '@/styles/colors';
import { Env } from '@/utils/env';
import styles from './LeftNavigationPanel.module.scss';
import { FullscreenNavItem } from './FullscreenNavItem';
import { ModuleRouteNavItem } from './ModuleRouteNavItem';
import { SubRouteNavItem } from './SubRouteNavItem';

export interface LeftNavigationPanelViewProps {
  className?: string;
  position?: 'absolute' | 'relative';
  parentRoute?: PageHref;
  showFullscreenToggle?: boolean;
}

// Temporarily hiding module names from the nav panel until a design decision is made.
// Show only locally in dev mode.
const HIDE_MODULES = !Env.isDev();

export const LeftNavigationPanelView: React.FC<LeftNavigationPanelViewProps> = ({
  className,
  position = 'relative',
  parentRoute,
  showFullscreenToggle = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname() as PageHref;
  const { organizationModel } = useCurrentOrganization();
  const { user } = useCurrentUser();
  const pageTitle = getPageTitle(pathname, { mode: PageTitleMode.ROOT });

  const permissions = useMemo(() => getPagesByUserPermissions({ user }), [user]);

  const moduleItems = useMemo(() => {
    if (!organizationModel) return [];
    return getNavigationRoutes(user.org_id)
      .filter((item) => !item.hidden && permissions[item.href])
      .map((item) => {
        const subRoutes =
          item.subRoutes
            ?.filter((sub) => !sub.hidden && permissions[sub.href])
            .map((sub) => ({ ...sub, disabled: 'disabled' in sub ? sub.disabled : !permissions[sub.href] })) || [];

        if (!subRoutes?.length) {
          // To show single module as a subRoute
          const moduleAsSubRoute: (typeof subRoutes)[number] = {
            href: item.href,
            label: item.label,
            iconName: item.iconName,
            disabled: false,
          };
          subRoutes.push(moduleAsSubRoute);
        }
        return {
          ...item,
          subRoutes: subRoutes.filter((sub) => !sub.disabled),
        };
      });
  }, [organizationModel, user, permissions]);

  const parentRouteSubItems = useMemo(() => {
    if (!parentRoute || !organizationModel) return null;
    const match = getNavigationRoutes(user.org_id).find((item) => item.href === parentRoute);
    if (!match?.subRoutes) return null;
    return match.subRoutes
      .filter((sub) => !sub.hidden && permissions[sub.href])
      .map((sub) => ({ ...sub, disabled: 'disabled' in sub ? sub.disabled : !permissions[sub.href] }));
  }, [parentRoute, organizationModel, user, permissions]);

  const activeModuleHref = useMemo(
    () => moduleItems.find((m) => (m.isActiveLink ? m.isActiveLink(pathname!) : pathname?.startsWith(m.href)))?.href,
    [moduleItems, pathname],
  );

  const handleModuleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  useLayoutEffect(() => {
    const width = isOpen ? 'var(--leftSidebarOpenedWidth)' : 'var(--leftSidebarWidth)';
    document.documentElement.style.setProperty('--leftNavCurrentWidth', width);

    return () => {
      document.documentElement.style.removeProperty('--leftNavCurrentWidth');
    };
  }, [isOpen]);

  const accordionItems = moduleItems.map((item) => (
    <div key={item.href} className="flex flex-col gap-2">
      {!HIDE_MODULES && (
        <ModuleRouteNavItem item={item} isActive={activeModuleHref === item.href} onToggle={handleModuleClick} />
      )}
      {!!item.subRoutes?.length && (
        <div className="flex flex-col gap-2">
          {item.subRoutes.map((sub) => (
            <SubRouteNavItem key={sub.href} item={sub} isOpen={isOpen} />
          ))}
        </div>
      )}
    </div>
  ));

  const controls = parentRouteSubItems
    ? parentRouteSubItems.map((sub) => <SubRouteNavItem key={sub.href} item={sub} isOpen={isOpen} />)
    : accordionItems;

  return (
    <div
      className={classnames(
        styles.root,
        { [styles.absolute]: position === 'absolute', [styles.open]: isOpen },
        className,
      )}
    >
      <div className="flex h-full flex-col">
        <div className={styles.toggleButton}>
          {isOpen && <div className={styles.pageTitle}>{pageTitle}</div>}
          <Button transparent onClick={() => setIsOpen(!isOpen)}>
            <Icon
              name="ArrowRightLineIcon"
              fillColor={Colors.Neutral}
              style={{ transform: !isOpen ? 'rotate(0deg)' : 'rotate(180deg)' }}
            />
          </Button>
        </div>
        <div className={classnames(styles.controls, 'flex flex-col gap-2')}>{controls}</div>
        {showFullscreenToggle && (
          <div className={styles.bottom}>
            <FullscreenNavItem isOpen={isOpen} />
          </div>
        )}
      </div>
    </div>
  );
};
